import {
  ACCESSIBILITY_ARGS,
  ACTION_ARGS,
  COMPONENT_STATE_ARGS,
  pickArgs,
} from '../../../../utils/spds/metadata';
import { ARRAY, BOOLEAN, STRING } from '../../../../utils/component-validations';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import DestructiveButton from './destructive.gjs';
import FlexColumn from '../../layout/flex/column.gjs';
import GhostButton from './ghost.gjs';
import Menu from './dropdown/menu.gjs';
import PrimaryButton from './primary.gjs';
import SecondaryButton from './secondary.gjs';
import Super from '../../core/base.gjs';
import TertiaryButton from './tertiary.gjs';
import Trigger from './dropdown/trigger.gjs';
import closeOnClickOutside from '../../../../modifiers/close-on-click-outside';

const DESTRUCTIVE = 'destructive';
const GHOST = 'ghost';
const PRIMARY = 'primary';
const SECONDARY = 'secondary';
const TERTIARY = 'tertiary';
export const BUTTON_TYPES = [DESTRUCTIVE, GHOST, PRIMARY, SECONDARY, TERTIARY];

const METADATA = {
  component: 'Spds::Action::Button::Dropdown',
  description: 'Button with dropdown menu that reveals additional options or actions',
  meta: {
    ...pickArgs(ACTION_ARGS, ['onClose', 'onOpen']).meta,
    ...pickArgs(ACCESSIBILITY_ARGS, ['ariaLabel']).meta,
    ...pickArgs(COMPONENT_STATE_ARGS, ['disabled']).meta,
    autofocus: {
      type: BOOLEAN,
      description: 'Automatically focus the button on render',
      defaultValue: false,
    },
    buttonType: {
      type: STRING,
      description: 'Visual style variant of the button',
      options: BUTTON_TYPES,
      defaultValue: 'primary',
    },
    label: {
      description: 'Text label displayed on the button',
      initialValue: 'Button',
      type: STRING,
    },
    menuItems: {
      type: ARRAY,
      description: 'Array of menu item objects with label, value, and optional onClick properties',
    },
    onItemClick: {
      ...ACTION_ARGS.meta.onClick,
      description: 'Action triggered when a menu item is clicked',
    },
    size: {
      type: STRING,
      description: 'Button size',
      options: ['normal', 'small'],
      defaultValue: 'normal',
    },
  },
  name: 'Dropdown Button',
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=26311-17009&m=dev',
    github:
      'https://github.com/simplepractice/ember-simplepractice/tree/staging/addon/src/components/spds/action/button/dropdown.gjs',
    zeroheight:
      'https://simplepracticedesign.zeroheight.com/styleguide/s/98005/p/87c0a6-dropdown-button',
  },
};

export default class SpdsActionButtonDropdown extends Super {
  static metadata = this.setMetadata(METADATA);
  baseClass = 'spds-button spds-action-button';
  variantClass = 'spds-action-button-dropdown';

  @tracked open = false;

  constructor() {
    super(...arguments);

    this.arguments = this.composeArguments(METADATA);
  }

  get button() {
    switch (this.args.buttonType) {
      case DESTRUCTIVE:
        return DestructiveButton;
      case GHOST:
        return GhostButton;
      case PRIMARY:
        return PrimaryButton;
      case SECONDARY:
        return SecondaryButton;
      case TERTIARY:
        return TertiaryButton;
      default:
        return PrimaryButton;
    }
  }

  get menuId() {
    return this.composeSubcomponentId('menu');
  }

  get trigger() {
    return this.triggerComponent?.baseElement;
  }

  get triggerId() {
    return this.composeSubcomponentId('trigger');
  }

  closeMenu = () => {
    this.open = false;
    this.args.onClose?.();
  };

  toggleOpenState = () => {
    this.open = !this.open;

    this.open ? this.args.onOpen?.() : this.args.onClose?.();
  };

  <template>
    <FlexColumn
      @id={{this.id}}
      class="spds-action-button-dropdown-container"
      data-test-spds-action-button-dropdown
      {{this.setupComponent}}
      {{this.validateArguments}}
      {{closeOnClickOutside this.closeMenu}}
    >
      <Trigger
        @autofocus={{@autofocus}}
        @button={{this.button}}
        @disabled={{@disabled}}
        @id={{this.triggerId}}
        @open={{this.open}}
        @registerComponent={{fn this.registerComponent "triggerComponent"}}
        @size={{@size}}
        @unregisterComponent={{fn this.unregisterComponent "triggerComponent"}}
        aria-controls={{this.menuId}}
        class={{this.classes}}
        {{on "click" this.toggleOpenState}}
        ...attributes
      >
        {{yield to="trigger"}}
        {{@label}}
      </Trigger>
      <Menu
        @anchorElement={{this.trigger}}
        @id={{this.menuId}}
        @open={{this.open}}
        @toggleOpenState={{this.toggleOpenState}}
        aria-labelledby={{this.triggerId}}
        as |menu|
      >
        {{#if (has-block "menu")}}
          {{yield (hash Item=(component menu.Item onClick=@onItemClick)) to="menu"}}
        {{else if @menuItems}}
          {{#each @menuItems as |item|}}
            <menu.Item
              @onClick={{or item.onClick @onItemClick}}
              @value={{or item.value item.label}}
            >
              {{item.label}}
            </menu.Item>
          {{/each}}
        {{/if}}
      </Menu>
    </FlexColumn>
  </template>
}

export const ACTION_BUTTON_DROPDOWN_METADATA = SpdsActionButtonDropdown.metadata;
